import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "./ui";
import { api, formatApiError } from "../../lib/api";

export function buildMailto(contact, tpl) {
  const subject = encodeURIComponent(tpl?.subject || "Kontaktaufnahme");
  const body = encodeURIComponent(tpl?.body || "");
  return `mailto:${contact?.email || "hilfe@mfmed.de"}?subject=${subject}&body=${body}`;
}

export default function Contact({ contact, emailTemplate, index, appr }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const dial = contact?.phone_dial || "+4962515504920";
  const wa = (contact?.whatsapp || "+4962515504920").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${wa}?text=${encodeURIComponent("Guten Tag, ich habe eine Anfrage zu Ihrem Leistungsangebot.")}`;
  const mailto = buildMailto(contact, emailTemplate);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Bitte geben Sie Ihren Namen und eine Nachricht an.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Ihre Anfrage wurde übermittelt. Wir melden uns zeitnah.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-white border border-[var(--mf-line)] px-4 py-3 text-[var(--mf-ink)] placeholder:text-[var(--mf-grey)] focus:border-[var(--mf-red)] transition-colors";

  return (
    <section id="kontakt" style={appr?.style} className={`py-20 lg:py-28 ${appr?.dark ? "mf-section-dark" : ""}`}>
      <div className="mf-container">
        <Reveal><SectionLabel index={index}>Kontakt</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-head font-light tracking-tight text-[1.875em] lg:text-[2.6em] leading-tight mt-6 text-[var(--mf-ink)] max-w-2xl">
            So erreichen Sie <span className="font-medium">mich</span>.
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* left: details + buttons */}
          <div>
            <div className="space-y-px border border-[var(--mf-line)]">
              <div className="flex items-start gap-4 p-6" data-testid="contact-address">
                <span className="w-11 h-11 bg-[var(--mf-ink)] text-white flex items-center justify-center shrink-0"><MapPin size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-[var(--mf-grey)]">Anschrift</span>
                  <span className="font-medium text-[var(--mf-ink)]">
                    {contact?.address_line1}<br />{contact?.address_line2}<br />{contact?.address_line3}
                  </span>
                </span>
              </div>
              <a href={`tel:${dial}`} data-testid="contact-phone" className="flex items-center gap-4 p-6 border-t border-[var(--mf-line)] group hover:bg-[var(--mf-mist)] transition-colors">
                <span className="w-11 h-11 bg-[var(--mf-red)] text-white flex items-center justify-center shrink-0"><Phone size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-[var(--mf-grey)]">Telefon</span>
                  <span className="font-head font-semibold text-lg text-[var(--mf-ink)]">{contact?.phone}</span>
                </span>
              </a>
              <a href={`mailto:${contact?.email}`} data-testid="contact-email" className="flex items-center gap-4 p-6 border-t border-[var(--mf-line)] group hover:bg-[var(--mf-mist)] transition-colors">
                <span className="w-11 h-11 bg-[var(--mf-ink)] text-white flex items-center justify-center shrink-0"><Mail size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-[var(--mf-grey)]">E-Mail</span>
                  <span className="font-head font-semibold text-lg text-[var(--mf-ink)] break-all">{contact?.email}</span>
                </span>
              </a>
              <div className="flex items-start gap-4 p-6 border-t border-[var(--mf-line)]">
                <span className="w-11 h-11 bg-[var(--mf-ink)] text-white flex items-center justify-center shrink-0"><Clock size={20} /></span>
                <span>
                  <span className="block text-xs uppercase tracking-wider text-[var(--mf-grey)]">Servicezeiten</span>
                  <span className="font-medium text-[var(--mf-ink)]">{contact?.service_hours}</span>
                </span>
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <a href={mailto} data-testid="contact-email-btn" className="mf-cta">
                <Mail size={18} /> Kontakt per E-Mail
              </a>
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
                 className="mf-cta-ghost" style={{ borderColor: "#25D366", color: "#128C4A" }}>
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
            <p className="mt-3 text-xs text-[var(--mf-grey)]">
              Der E-Mail-Button öffnet Ihr Programm mit dem Betreff „{emailTemplate?.subject}“ und einer Textvorlage.
            </p>
          </div>

          {/* right: form */}
          <div>
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-[var(--mf-mist)] border border-[var(--mf-line)] p-6 lg:p-8">
                <h3 className="font-head font-bold text-lg text-[var(--mf-ink)]">Kontaktformular</h3>
                <p className="text-sm text-[var(--mf-grey)] mt-1">Vier Felder genügen. Ihre Anfrage wird bei uns gespeichert.</p>
                <div className="mt-6 space-y-4">
                  <input data-testid="form-name" className={field} placeholder="Name *" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="form-email" type="email" className={field} placeholder="E-Mail" value={form.email}
                           onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <input data-testid="form-phone" className={field} placeholder="Telefon" value={form.phone}
                           onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <textarea data-testid="form-message" rows={5} className={field} placeholder="Ihre Nachricht *" value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  <button type="submit" data-testid="form-submit" disabled={sending} className="mf-cta w-full disabled:opacity-60">
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    {sending ? "Wird gesendet …" : "Absenden"}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
